/**
 * Product box presets — keyed by the Size attribute value
 * exactly as entered in the Attribute manager.
 *
 * Each preset maps to:
 *   sizePerItem  — area (m²) or length (m) of ONE piece
 *   itemsPerBox  — pieces per carton
 *   sizePerBox   — auto-calculated: sizePerItem × itemsPerBox
 *
 * To add a new preset: add one entry to the matching section.
 * The key must exactly match the option value in the Size attribute.
 */

// ── m² presets (floor/wall tiles) ─────────────────────────────
const M2_PRESETS = {
  "60×60":   { width: 0.60, length: 0.60, itemsPerBox: 4  },
  "60x60":   { width: 0.60, length: 0.60, itemsPerBox: 4  }, // alternate notation
  "60*60":   { width: 0.60, length: 0.60, itemsPerBox: 4  },
  "60×120":  { width: 0.60, length: 1.20, itemsPerBox: 3  },
  "60x120":  { width: 0.60, length: 1.20, itemsPerBox: 3  },
  "60*120":  { width: 0.60, length: 1.20, itemsPerBox: 3  },
  "120×60":  { width: 0.60, length: 1.20, itemsPerBox: 3  },
  "120x60":  { width: 0.60, length: 1.20, itemsPerBox: 3  },
  "30×60":   { width: 0.30, length: 0.60, itemsPerBox: 8  },
  "30x60":   { width: 0.30, length: 0.60, itemsPerBox: 8  },
  "30*60":   { width: 0.30, length: 0.60, itemsPerBox: 8  },
  "40×40":   { width: 0.40, length: 0.40, itemsPerBox: 6  },
  "40x40":   { width: 0.40, length: 0.40, itemsPerBox: 6  },
  "30×30":   { width: 0.30, length: 0.30, itemsPerBox: 11 },
  "30x30":   { width: 0.30, length: 0.30, itemsPerBox: 11 },
  "50×50":   { width: 0.50, length: 0.50, itemsPerBox: 5  },
  "50x50":   { width: 0.50, length: 0.50, itemsPerBox: 5  },
  "80×80":   { width: 0.80, length: 0.80, itemsPerBox: 3  },
  "80x80":   { width: 0.80, length: 0.80, itemsPerBox: 3  },
  "120×120": { width: 1.20, length: 1.20, itemsPerBox: 2  },
  "120x120": { width: 1.20, length: 1.20, itemsPerBox: 2  },
  "20×60":   { width: 0.20, length: 0.60, itemsPerBox: 10 },
  "20x60":   { width: 0.20, length: 0.60, itemsPerBox: 10 },
  "10×60":   { width: 0.10, length: 0.60, itemsPerBox: 12 },
  "10x60":   { width: 0.10, length: 0.60, itemsPerBox: 12 },
};

// ── linear_meter presets (skirting, border strips) ─────────────
const LINEAR_PRESETS = {
  "60cm":  { lengthPerPiece: 0.60, itemsPerBox: 6 },
  "0.6m":  { lengthPerPiece: 0.60, itemsPerBox: 6 },
  "1.0m":  { lengthPerPiece: 1.00, itemsPerBox: 6 },
  "100cm": { lengthPerPiece: 1.00, itemsPerBox: 6 },
  "1.2m":  { lengthPerPiece: 1.20, itemsPerBox: 6 },
  "120cm": { lengthPerPiece: 1.20, itemsPerBox: 6 },
  "80cm":  { lengthPerPiece: 0.80, itemsPerBox: 6 },
  "40cm":  { lengthPerPiece: 0.40, itemsPerBox: 8 },
};

// ── pcs presets (items sold by count) ─────────────────────────
const PCS_PRESETS = {
  "3 / box": { itemsPerBox: 3 },
  "4 / box": { itemsPerBox: 4 },
  "6 / box": { itemsPerBox: 6 },
  "8 / box": { itemsPerBox: 8 },
  "10 / box":{ itemsPerBox: 10 },
  "12 / box":{ itemsPerBox: 12 },
};

/**
 * Given a Size attribute value and the product's measurementUnit,
 * returns the three box fields ready to save to DB,
 * or null if no matching preset exists.
 *
 * @param {string} sizeValue      — the selected Size attribute value
 *                                  e.g. "60×60" or "60cm"
 * @param {string} measurementUnit — "m2" | "linear_meter" | "pcs"
 * @returns {{ sizePerItem, itemsPerBox, sizePerBox } | null}
 */
export function getPresetFields(sizeValue, measurementUnit) {
  if (!sizeValue) return null;

  // Normalize: trim whitespace
  const key = sizeValue.trim();

  if (measurementUnit === "m2") {
    const p = M2_PRESETS[key];
    if (!p) return null;
    const sizePerItem = +(p.width * p.length).toFixed(4);
    return {
      sizePerItem,
      itemsPerBox: p.itemsPerBox,
      sizePerBox:  +(sizePerItem * p.itemsPerBox).toFixed(4),
      // For display in the W×L toggle
      _width:  p.width,
      _length: p.length,
    };
  }

  if (measurementUnit === "linear_meter") {
    const p = LINEAR_PRESETS[key];
    if (!p) return null;
    return {
      sizePerItem: p.lengthPerPiece,
      itemsPerBox: p.itemsPerBox,
      sizePerBox:  +(p.lengthPerPiece * p.itemsPerBox).toFixed(4),
      _width:  null,
      _length: null,
    };
  }

  if (measurementUnit === "pcs") {
    const p = PCS_PRESETS[key];
    if (!p) return null;
    return {
      sizePerItem: null,
      itemsPerBox: p.itemsPerBox,
      sizePerBox:  null,
      _width:  null,
      _length: null,
    };
  }

  return null;
}

/**
 * Check if a Size value has a matching preset for the given unit.
 */
export function hasPreset(sizeValue, measurementUnit) {
  return getPresetFields(sizeValue, measurementUnit) !== null;
}